import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Group, NumberInput, Stack, Switch, Text } from "@mantine/core";
import { PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { RingMeButton } from "@/components/doorbell/ring-me-button";
import {
  DOORBELL_COMPANY_SETTINGS_KEY,
  callApiError,
  doorbellCallsApi,
  type DoorbellCompanySettings,
} from "@/lib/api/doorbell-calls";

export const RING_SECONDS_MIN = 20;
export const RING_SECONDS_MAX = 90;

export function ringSecondsValid(v: number | string): v is number {
  return typeof v === "number" && Number.isInteger(v) && v >= RING_SECONDS_MIN && v <= RING_SECONDS_MAX;
}

interface ViewProps {
  settings: DoorbellCompanySettings;
  saving: boolean;
  onSave: (settings: DoorbellCompanySettings) => void;
  /** Only the settings, without the card's title and intro (the guide step says it). */
  bare?: boolean;
}

/** Live calls, Visitors can pick a person and Ring time — presentational. */
export function DoorbellCallSettingsView({ settings, saving, onSave, bare = false }: ViewProps) {
  const [enabled, setEnabled] = useState(settings.doorbell_calls_enabled);
  const [pickPerson, setPickPerson] = useState(settings.doorbell_person_selection);
  const [ring, setRing] = useState<number | string>(settings.doorbell_ring_seconds);
  useEffect(() => {
    setEnabled(settings.doorbell_calls_enabled);
    setPickPerson(settings.doorbell_person_selection);
    setRing(settings.doorbell_ring_seconds);
  }, [settings]);

  const valid = ringSecondsValid(ring);
  const changed =
    enabled !== settings.doorbell_calls_enabled ||
    pickPerson !== settings.doorbell_person_selection ||
    ring !== settings.doorbell_ring_seconds;

  const fields = (
    <Stack gap="sm">
      <Switch
        label="Live calls"
        description="Visitors at reception call your company here in the portal. The reception tablet must have calls turned on too."
        checked={enabled}
        disabled={saving}
        onChange={(e) => setEnabled(e.currentTarget.checked)}
      />
      <Switch
        label="Visitors can pick a person"
        description="Off: visitors only choose your company, for calls and for messages."
        checked={pickPerson}
        disabled={saving}
        onChange={(e) => setPickPerson(e.currentTarget.checked)}
      />
      <NumberInput
        label="Ring time"
        description="How long a call rings before the visitor can leave a message instead."
        suffix=" s"
        min={RING_SECONDS_MIN}
        max={RING_SECONDS_MAX}
        allowDecimal={false}
        value={ring}
        onChange={setRing}
        error={valid ? undefined : `Between ${RING_SECONDS_MIN} and ${RING_SECONDS_MAX} seconds`}
        disabled={saving}
        maw={160}
      />
      <Group justify="flex-end">
        <Button
          loading={saving}
          disabled={!changed || !valid}
          onClick={() => {
            if (ringSecondsValid(ring)) {
              onSave({ doorbell_calls_enabled: enabled, doorbell_person_selection: pickPerson, doorbell_ring_seconds: ring });
            }
          }}
        >
          Save
        </Button>
      </Group>
    </Stack>
  );

  if (bare) return <Stack data-testid="doorbell-call-settings">{fields}</Stack>;
  return (
    <Card withBorder padding="lg" data-testid="doorbell-call-settings">
      <Stack gap="sm">
        <Group gap="xs" wrap="nowrap">
          <PhoneCall size={18} />
          <Text fw={500}>Visitor calls</Text>
        </Group>
        <Text fz="sm" c="dimmed">
          When a visitor at reception picks your company, the people who get visitor calls are rung in the portal. They
          see and talk to the visitor, and can open the entrance door.
        </Text>
        {fields}
        {/* A test call to yourself, once calls are on and saved (the guide has its own Ring me step). */}
        {settings.doorbell_calls_enabled && (
          <Stack gap={4} data-testid="doorbell-ring-me">
            <Text fz="sm" c="dimmed">
              Check that ringing, notifications, the camera and the microphone work: this portal and your phone ring.
            </Text>
            <RingMeButton />
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

/** Settings page card for company admins; also the "Answer visitor calls" guide's first step (`onSaved` ticks it). */
export function DoorbellCallSettingsCard({ onSaved, bare = false }: { onSaved?: () => void; bare?: boolean } = {}) {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: DOORBELL_COMPANY_SETTINGS_KEY,
    queryFn: () => doorbellCallsApi.getCompanySettings().then((r) => r.data),
    retry: false,
    staleTime: 30_000,
  });
  const save = useMutation({
    mutationFn: (settings: DoorbellCompanySettings) => doorbellCallsApi.saveCompanySettings(settings).then((r) => r.data),
    onSuccess: (out) => {
      qc.setQueryData(DOORBELL_COMPANY_SETTINGS_KEY, out);
      toast.success("Visitor call settings saved");
      onSaved?.();
    },
    onError: (err) => toast.error(callApiError(err, "Couldn't save the visitor call settings.")),
  });
  if (isLoading || isError || !data) return null;
  return <DoorbellCallSettingsView settings={data} saving={save.isPending} onSave={(s) => save.mutate(s)} bare={bare} />;
}
